import { order2nside, pix2vec_nest, query_disc_inclusive_nest } from '@hscmap/healpix';
import { StarCatalog, bvToKelvin, kelvinToRgb, srgbToLinear } from '../../data/catalog';
import type { Vec3 } from '../../lib/math';
import type { GL, Program } from '../gl';
import { StarLayer } from './stars';

// Faint stars from Gaia DR3 (V 10 to about 13), in HEALPix tiles that load as you zoom in, drawn like the
// app's other stars. Built by scripts/build-gaia.mjs; ESA/Gaia/DPAC, CC BY-NC 3.0 IGO.

const base = () => `${import.meta.env.BASE_URL}stars/gaia/`;

/** B−V from Gaia BP−RP, by inverting Gaia's DR3 relation (valid for B−V from −0.5 to 3.5). */
export const BV_FROM_BYTE = (() => {
  const f = (x: number) => 0.06483 + 1.575 * x - 0.7815 * x * x + 0.5707 * x ** 3 - 0.176 * x ** 4 + 0.01916 * x ** 5;
  const out = new Float32Array(256);
  for (let k = 0; k < 256; k++) {
    if (k === 255) {
      out[k] = 0.65; // unknown colour: a Sun-like star
      continue;
    }
    const target = k / 40 - 0.5;
    let lo = -0.5, hi = 3.5;
    for (let i = 0; i < 40; i++) {
      const m = (lo + hi) / 2;
      if (f(m) < target) lo = m;
      else hi = m;
    }
    out[k] = (lo + hi) / 2;
  }
  return out;
})();

export function decode(buf: ArrayBuffer, pix: number, nside: number): StarCatalog {
  const dv = new DataView(buf);
  if (String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3)) !== 'GAI1') throw new Error('bad Gaia tile');
  const n = dv.getUint32(4, true), scale = dv.getFloat32(8, true);
  const x = new Int16Array(buf.slice(12, 12 + n * 2)), y = new Int16Array(buf.slice(12 + n * 2, 12 + n * 4));
  const m8 = new Uint8Array(buf, 12 + n * 4, n), c8 = new Uint8Array(buf, 12 + n * 5, n);
  const c = pix2vec_nest(nside, pix);
  const up = Math.abs(c[2]) > 0.99 ? [1, 0, 0] : [0, 0, 1];
  let e = [up[1] * c[2] - up[2] * c[1], up[2] * c[0] - up[0] * c[2], up[0] * c[1] - up[1] * c[0]];
  const el = Math.hypot(e[0], e[1], e[2]);
  e = [e[0] / el, e[1] / el, e[2] / el];
  const nn = [c[1] * e[2] - c[2] * e[1], c[2] * e[0] - c[0] * e[2], c[0] * e[1] - c[1] * e[0]];
  const dir = new Float32Array(n * 3), mag = new Float32Array(n), bv = new Float32Array(n), color = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const u = (x[i] / 32767) * scale, v = (y[i] / 32767) * scale;
    const px = c[0] + u * e[0] + v * nn[0], py = c[1] + u * e[1] + v * nn[1], pz = c[2] + u * e[2] + v * nn[2];
    const l = Math.hypot(px, py, pz);
    dir[i * 3] = px / l;
    dir[i * 3 + 1] = py / l;
    dir[i * 3 + 2] = pz / l;
    mag[i] = 10 + m8[i] / 40;
    bv[i] = BV_FROM_BYTE[c8[i]];
    const rgb = kelvinToRgb(bvToKelvin(bv[i]));
    color[i * 3] = srgbToLinear(rgb[0]);
    color[i * 3 + 1] = srgbToLinear(rgb[1]);
    color[i * 3 + 2] = srgbToLinear(rgb[2]);
  }
  return { count: n, dir, mag, bv, hip: new Uint32Array(n), color, info: new Map(), named: [], byHip: new Map() };
}

export class GaiaStars {
  private index: { order: number; counts: number[]; version?: string } | null = null;
  private tiles = new Map<number, { layer: StarLayer | null; used: number }>();
  private frame = 0;
  busy = false;

  constructor(private gl: GL) {
    void fetch(`${base()}index.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => (this.index = j))
      .catch(() => {});
  }

  private async load(pix: number, nside: number) {
    this.tiles.set(pix, { layer: null, used: this.frame });
    try {
      const r = await fetch(`${base()}${pix}.gaia${this.index?.version ? `?v=${this.index.version}` : ''}`);
      if (!r.ok || !r.body) throw new Error(String(r.status));
      const raw = await new Response(r.body.pipeThrough(new DecompressionStream('gzip'))).arrayBuffer();
      const cat = decode(raw, pix, nside);
      const layer = new StarLayer(this.gl, cat);
      layer.setMagnitudes(cat.mag);
      this.tiles.set(pix, { layer, used: this.frame });
      if (this.tiles.size > 40) {
        const old = [...this.tiles.entries()].filter(([, t]) => t.layer).sort((a, b) => a[1].used - b[1].used);
        for (const [k, t] of old.slice(0, this.tiles.size - 32)) {
          t.layer?.dispose?.();
          this.tiles.delete(k);
        }
      }
    } catch {
      setTimeout(() => this.tiles.delete(pix), 60000); // try again later
    }
  }

  draw(setCommon: (p: Program) => void, view: { centre: Vec3; radius: number }, u: Parameters<StarLayer['draw']>[1]) {
    if (!this.index) return;
    this.frame++;
    const nside = order2nside(this.index.order);
    const cells: number[] = [];
    query_disc_inclusive_nest(nside, view.centre as [number, number, number], Math.min(Math.PI, view.radius * 1.03), (n) => cells.push(n));
    let loading = 0;
    for (const pix of cells) {
      if (!this.index.counts[pix]) continue;
      const t = this.tiles.get(pix);
      if (!t) {
        if (loading < 4) void this.load(pix, nside);
        loading++;
        continue;
      }
      t.used = this.frame;
      if (!t.layer) {
        loading++;
        continue;
      }
      t.layer.draw(setCommon, u);
    }
    this.busy = loading > 0;
  }
}
